import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { sprekersApi } from '../api';
import SharingControl from './SharingControl';
import type { RecordVisibility, SpeakerNote } from '../types';

interface FormState {
  note: string;
  visibility: RecordVisibility;
  shared_with_user_ids: string[];
}

const EMPTY_FORM: FormState = { note: '', visibility: 'prive', shared_with_user_ids: [] };

interface Props {
  engagementId: string;
  currentUserId: string;
  canEdit: boolean;
}

/**
 * Privé-sprekersnotities: standaard alleen zichtbaar voor de auteur, nooit
 * automatisch op de beamer of in Centrale Planning (docs/sprekers-Module.md
 * sectie 12). De backend filtert al op zichtbaarheid -- deze tab toont enkel
 * wat de API teruggeeft.
 */
export default function NotesTab({ engagementId, currentUserId, canEdit }: Props) {
  const [notes, setNotes] = useState<SpeakerNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | 'new' | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  const fetchNotes = async () => {
    try {
      const data = await sprekersApi.listNotes(engagementId);
      setNotes(data);
    } catch (error) {
      toast.error('Kon notities niet laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engagementId]);

  const startEdit = (note: SpeakerNote) => {
    setForm({ note: note.note, visibility: note.visibility, shared_with_user_ids: note.shared_with_user_ids });
    setEditingId(note.id);
  };

  const startNew = () => {
    setForm(EMPTY_FORM);
    setEditingId('new');
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.note.trim()) return;
    setSubmitting(true);
    try {
      if (editingId === 'new') {
        await sprekersApi.createNote(engagementId, form);
      } else if (editingId) {
        await sprekersApi.updateNote(editingId, form);
      }
      setEditingId(null);
      fetchNotes();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Opslaan mislukt');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (noteId: string) => {
    if (!window.confirm('Weet je zeker dat je deze notitie wilt verwijderen?')) return;
    try {
      await sprekersApi.deleteNote(noteId);
      fetchNotes();
    } catch (error) {
      toast.error('Verwijderen mislukt');
    }
  };

  if (loading) return <p className="text-sm text-slate-500">Laden...</p>;

  if (editingId) {
    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        <Textarea
          value={form.note}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setForm({ ...form, note: e.target.value })}
          rows={6}
          placeholder="Persoonlijke herinnering, timing, uitspraaknotitie..."
          required
        />
        <SharingControl
          visibility={form.visibility}
          sharedWithUserIds={form.shared_with_user_ids}
          onChange={(visibility, shared_with_user_ids) => setForm({ ...form, visibility, shared_with_user_ids })}
        />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => setEditingId(null)}>Annuleren</Button>
          <Button type="submit" disabled={submitting || !form.note.trim()}>Opslaan</Button>
        </div>
      </form>
    );
  }

  return (
    <div className="space-y-3">
      {notes.length === 0 && <p className="text-sm text-slate-500">Nog geen privé-notities.</p>}
      {notes.map((note) => (
        <div key={note.id} className="rounded-lg border border-slate-100 p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <Badge className={note.visibility === 'gedeeld' ? 'bg-teal-100 text-teal-700' : 'bg-slate-100 text-slate-600'}>
                {note.visibility === 'gedeeld' ? 'Gedeeld' : 'Privé'}
              </Badge>
              <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">{note.note}</p>
            </div>
            {note.author_id === currentUserId && (
              <div className="flex shrink-0 gap-1">
                <Button variant="ghost" size="icon" onClick={() => startEdit(note)} aria-label="Bewerken">
                  <Pencil className="h-4 w-4 text-slate-400" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => handleDelete(note.id)} aria-label="Verwijderen">
                  <Trash2 className="h-4 w-4 text-slate-400" />
                </Button>
              </div>
            )}
          </div>
        </div>
      ))}

      {canEdit && (
        <Button variant="outline" onClick={startNew}>
          <Plus className="mr-2 h-4 w-4" />
          Notitie toevoegen
        </Button>
      )}
    </div>
  );
}
